import { INewMembershipInfo, RoleType } from '@lib/types';
import { AssociateInfo, NewMembershipFormData } from '../application/models/NewJoinSessionData';

export const validateMembershipFormData: (data: NewMembershipFormData) => {
  [section: string]: { [field: string]: string[] };
} = (data) => {
  const errorOutput: { [section: string]: { [field: string]: string[] } } = {};

  const checkError: (section: string, field: string, error: string | null) => void = (section, field, error) => {
    if (error != null) {
      if (errorOutput[section] === undefined) {
        errorOutput[section] = {};
      }
      errorOutput[section][field] = [error];
    }
  };

  checkError('contact', 'firstName', validateName(data.firstName ?? ''));
  checkError('contact', 'lastName', validateName(data.lastName ?? ''));
  checkError('contact', 'email', validateEmail(data.email ?? ''));
  checkError('contact', 'gender', validateGender(data.gender ?? null));
  if (data.mobileConsent === null) {
    checkError('contact', 'mobileConsent', 'Mobile consent must have a value');
  }

  checkError('contact', 'mobileNumber', validateMobile(data.mobileNumber ?? ''));
  checkError('residenceAddress', 'streetAddress', validateAddress(data.residenceAddress.streetAddress ?? ''));
  checkError('residenceAddress', 'city', validateAddress(data.residenceAddress.city ?? ''));
  checkError('residenceAddress', 'state', validateState(data.residenceAddress.state ?? ''));
  checkError('residenceAddress', 'zipCode', validateZipCode(data.residenceAddress.zipCode ?? ''));
  if (data.mailingAddress !== undefined) {
    checkError('mailingAddress', 'streetAddress', validateAddress(data.mailingAddress.streetAddress ?? ''));
    checkError('mailingAddress', 'city', validateAddress(data.mailingAddress.city ?? ''));
    checkError('mailingAddress', 'state', validateState(data.mailingAddress.state ?? ''));
    checkError('mailingAddress', 'zipCode', validateZipCode(data.mailingAddress.zipCode ?? ''));
  } else {
  }

  checkError('registration', 'email', validateEmail(data.email ?? ''));
  checkError('registration', 'password', validatePassword(data.password ?? '', data.email ?? ''));
  checkError(
    'registration',
    'confirmPassword',
    validateConfirmPassword(data.password ?? '', data.confirmPassword ?? '')
  );

  return errorOutput;
};

export const validateAssociateFormData: (data: AssociateInfo) => {
  [section: string]: { [field: string]: string[] };
} = (data) => {
  const errorOutput: { [section: string]: { [field: string]: string[] } } = {};

  const checkError: (section: string, field: string, error: string | null) => void = (section, field, error) => {
    if (error != null) {
      if (errorOutput[section] === undefined) {
        errorOutput[section] = {};
      }
      errorOutput[section][field] = [error];
    }
  };

  checkError('addAssociate', 'firstName', validateName(data.firstName ?? ''));
  checkError('addAssociate', 'lastName', validateName(data.lastName ?? ''));
  checkError('addAssociate', 'gender', validateGender(data.gender ?? null));
  // checkError('addAssociate', 'dateOfBirth', validateDOB(data.dateOfBirth ?? null));

  return errorOutput;
};

// Intended for use with first and/or last name.  Allows spaces within the name.  Allows blocks of multiple spaces.
// Does not allow any characters other than alphabet (upper and lower) and spaces.  No hyphens.  No apostrophes.
export const validateName: (name: string) => string | null = (name) => {
  if ((name?.trim() ?? '').length === 0) {
    return 'Required';
  }
  return null;
};

export const validateGender: (gender: string | null) => string | null = (gender) => {
  if (gender == null) {
    return 'Required';
  }
  return null;
};

export const validateDOB: (dateOfBirth: string | null) => string | null = (dateOfBirth) => {
  if (dateOfBirth == '') {
    return 'Required';
  }
  return null;
};

export const validateEmail: (email: string) => string | null = (email) => {
  if ((email?.trim() ?? '').length === 0) {
    return 'Required';
  } else {
    const emailRegex = /^\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b$/i;
    if (emailRegex.test(email.toUpperCase()) === false) {
      return 'Must be a valid email address';
    }
  }
  return null;
};

export const validateMobile: (mobile: string) => string | null = (mobile) => {
  if ((mobile?.trim() ?? '').length === 0) {
    return 'Required';
  }
  const mobileNumber: string = mobile.replace(/\D/g, '');
  const hasInvalidZeros: boolean = mobileNumber.slice(0, 3) === '000' || mobileNumber.slice(3, 6) === '000' || mobileNumber.slice(6, 10) === '0000' || mobileNumber === '0000000000';
  if (hasInvalidZeros) {
    return 'Please enter a valid phone number';   
  }
  else if (mobileNumber.length < 10) {
    return 'Phone number must be 10 digits';
  }
  return null;
};

export const validateAddress: (address: string) => string | null = (address) => {
  if ((address?.trim() ?? '').length === 0) {
    return 'Required';
  }
  return null;
};

export const validateState: (state: string) => string | null = (state) => {
  const validStates = [
    'AL',
    'AK',
    'AZ',
    'AR',
    'CA',
    'CO',
    'CT',
    'DE',
    'DC',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'PR',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ];

  if ((state?.trim() ?? '').length === 0) {
    return 'Required';
  } else {
    if (state.length === 2 && !validStates.includes(state)) {
      return 'Please enter a valid state';
    }
  }
  return null;
};

export const validateZipCode: (zip: string) => string | null = (zip) => {
  if ((zip?.trim() ?? '').length === 0) {
    return 'Required';
  } else {
    if (zip.replace(/\D/g, '').length < 5) {
      return 'ZIP Code must be at least 5 digits';
    } else {
      if (zip.replace(/\D/g, '').length > 5 && zip.replace(/\D/g, '').length < 9) {
        return 'Please complete valid add-on codes';
      }
    }
  }
  return null;
};

export const validatePassword: (password: string, email: string) => string | null = (password, email) => {
  if ((password?.trim() ?? '').length === 0) {
    return 'Password is required.';
  } else {
    //between 8-31 characters
    if (password.length < 8) {
      return 'Your password should be at least 8 characters';
    }
    //contains at least 1 number and 1 letter
    const numberLetterRegex = /^(?=.*[0-9])(?=.*[a-zA-Z]).+$/;
    if (numberLetterRegex.test(password) === false) {
      return 'Your password must have at least 1 number and 1 letter';
    }
    // Can include: ^ - ! @ # { } ~ $ _ .
    // if (password.match(/\`|%|&|\*|\(|\)|\+|\=|\[|\]|\\|\||;|'|\:|"|,|\.|\?|\<|\>|\)/)) {
    const specialCharactersRegex = /^[a-zA-Z0-9^!@#{}~$_.-]+$/;
    if (specialCharactersRegex.test(password) === false) {
      return 'Your password can only include these symbols: ^ - ! @ # { } ~ $ _ .';
    }
    // Cannot contain the word "password"
    if (password.toLowerCase().includes('password')) {
      return 'Your password cannot contain the word password';
    }
    // Cannot be your username
    if (password === email) {
      return 'Your password cannot be your username';
    }
  }
  return null;
};

export const validateConfirmPassword: (password: string, confirmPassword: string) => string | null = (
  password,
  confirmPassword
) => {
  if ((password?.trim() ?? '').length === 0) {
    return 'Please confirm your password';
  } else {
    if (password !== confirmPassword) {
      return 'Confirm password does not match the password you entered';
    }
  }
  return null;
};

interface IValidateAssociates {
  hasReachedMaxAssociates: boolean;
  hasReachedMaxAdults?: boolean;
  hasReachedMaxDependents?: boolean;
}

export const validateAssociates = (
  membershipInfo?: INewMembershipInfo,
  formData?: NewMembershipFormData
): IValidateAssociates => {
  const AS = membershipInfo?.roles?.find((role) => role.role === RoleType.ASSOCIATE);

  if (AS) {
    const numberOfAssociates = formData?.associates?.length || 0;
    const hasReachedMaxAssociates = numberOfAssociates >= AS.maximumAllowed;
    return {
      hasReachedMaxAssociates,
    };
  }

  const DE = membershipInfo?.roles?.find((role) => role.role === RoleType.DEPENDENT)!;
  const AD = membershipInfo?.roles?.find((role) => role.role === RoleType.ADULT_ASSOCIATE)!;

  const numberOfDependents = formData?.associates?.filter((a) => a.roleType === RoleType.DEPENDENT)?.length!;
  const numberOfAdults = formData?.associates?.filter((a) => a.roleType === RoleType.ADULT_ASSOCIATE)?.length!;

  const hasReachedMaxDependents = numberOfDependents >= DE?.maximumAllowed;
  const hasReachedMaxAdults = numberOfAdults >= AD?.maximumAllowed;
  const hasReachedMaxAssociates = hasReachedMaxAdults && hasReachedMaxDependents;

  return {
    hasReachedMaxAdults,
    hasReachedMaxDependents,
    hasReachedMaxAssociates,
  };
};
